/**
 * Line types (W1-10a; DESIGN.md §5.5 "Line types", §6.13 derived line kind, rpe-format F§9.5, F§10.5).
 *
 * The kind Phira draws is derived from the data (texture + which tracks exist), so changing the type means adding
 * and removing tracks and the texture:
 *   line    no texture, no text / paint / gif tracks
 *   text    no texture, a text track (no paint track: paint wins over text on line.png lines)
 *   image   a texture, no gif track, no auto-play (a .gif then shows its first frame, like Phira)
 *   gif     a GIF texture plus a gif progress track or "Play automatically" (`gifAutoLoop`)
 *   paint   no texture, a paint (brush) track
 *   uiMover attachUI set; refused while the line has notes; opacity 255 added when absent (F§10.5)
 * GIF invariant: gif data (track or auto-play) exists only on a GIF texture Phira decodes. Every texture change that
 * leaves the line without a GIF texture removes the gif track and `gifAutoLoop` in the same transaction (gifEvents
 * on a PNG/JPG make Phira fail to load the chart).
 */

import { isGifAsset } from '../model/lineKind';
import type { AssetId, AssetRef, Line, LineId, UiElement } from '../model/types';
import {
  assetOf,
  deletePropTracks,
  ensurePropTrack,
  hasTrack,
  lineLabel,
  lineOf,
  type Patch,
  type Tx,
} from './structureKit';
import type { LineCommands, LineTypeChoice } from './types';

/** Default text of a new text line. */
export const DEFAULT_TEXT = 'Text';
/** Default brush size (device px) of a new paint line. */
export const DEFAULT_BRUSH_PX = 10;
/** Opacity a UI mover needs to show its element (absent alpha = invisible, F§10.5). */
export const UI_MOVER_ALPHA = 255;

export const UI_ELEMENTS: readonly UiElement[] = ['pause', 'combonumber', 'combo', 'score', 'bar', 'name', 'level'];

/** True for assets a line can show as an image (texture or GIF; illustrations are images too). */
export function isImageAsset(a: AssetRef): boolean {
  if (a.role === 'texture' || a.role === 'gif' || a.role === 'illustration') return true;
  return a.mime.startsWith('image/');
}

function checkImage(tx: Tx, id: AssetId): AssetRef {
  const a = assetOf(tx, id);
  if (!isImageAsset(a)) tx.fail({ key: 'cmd.lines.notImage', params: { path: a.path } });
  return a;
}

function checkElement(tx: Tx, e: UiElement): void {
  if (!UI_ELEMENTS.includes(e)) tx.fail({ key: 'cmd.lines.uiElementRequired' });
}

/** Fails while the line has notes (UI movers never draw notes). */
function refuseNotes(tx: Tx, line: Line): void {
  const count = tx.idx.notesByLine(line.id).length;
  if (count > 0) tx.fail({ key: 'cmd.lines.uiMoverNotes', params: { count, line: lineLabel(tx, line) } });
}

/** Removes gif data (track and auto-play) unless the line's texture is a GIF; notes the removal. */
export function dropGifDataUnlessGif(tx: Tx, lineId: LineId): void {
  const line = tx.doc.lines.get(lineId);
  if (line === undefined) return;
  const gif = line.texture !== null && isGifAsset(tx.doc.assets.get(line.texture));
  if (gif) return;
  const removed = deletePropTracks(tx, lineId, 'gif');
  if (line.gifAutoLoop) tx.update('lines', lineId, { gifAutoLoop: false });
  if (removed > 0 || line.gifAutoLoop)
    tx.note({ key: 'cmd.lines.gifDataRemoved', params: { line: lineLabel(tx, line) } });
}

/** Sets the texture and applies the GIF rule. */
export function applyTexture(tx: Tx, lineId: LineId, asset: AssetId | null): void {
  const line = lineOf(tx, lineId);
  if (line.texture !== asset) tx.update('lines', lineId, { texture: asset });
  dropGifDataUnlessGif(tx, lineId);
}

/** UI mover: refuses notes, sets the element and makes sure the element is visible (opacity 255 when absent). */
function applyUiMover(tx: Tx, line: Line, element: UiElement): void {
  checkElement(tx, element);
  refuseNotes(tx, line);
  if (line.attachUI !== element) tx.update('lines', line.id, { attachUI: element });
  if (ensurePropTrack(tx, line.id, 'alpha', UI_MOVER_ALPHA)) {
    tx.note({ key: 'cmd.lines.uiMoverAlphaAdded', params: { line: lineLabel(tx, line) } });
  }
}

const setLineType: LineCommands['setLineType'] = (tx: Tx, { line: lineId, type, texture, attachUI, text }) => {
  const line = lineOf(tx, lineId);
  const patch: Patch<Line> = {};
  const dropKinds = (props: readonly ('text' | 'paint' | 'gif')[]): void => {
    for (const p of props) deletePropTracks(tx, lineId, p);
  };
  const t: LineTypeChoice = type;
  switch (t) {
    case 'line':
    case 'text':
    case 'paint': {
      patch.texture = null;
      patch.gifAutoLoop = false;
      patch.attachUI = null;
      tx.update('lines', lineId, patch);
      if (t === 'line') dropKinds(['text', 'paint', 'gif']);
      else if (t === 'text') {
        dropKinds(['paint', 'gif']);
        ensurePropTrack(tx, lineId, 'text', text ?? DEFAULT_TEXT);
      } else {
        dropKinds(['text', 'gif']);
        ensurePropTrack(tx, lineId, 'paint', DEFAULT_BRUSH_PX);
      }
      break;
    }
    case 'image': {
      const asset = texture ?? line.texture;
      if (asset === null) tx.fail({ key: 'cmd.lines.textureRequired' });
      checkImage(tx, asset);
      tx.update('lines', lineId, { texture: asset, gifAutoLoop: false, attachUI: null });
      dropKinds(['text', 'paint', 'gif']);
      break;
    }
    case 'gif': {
      const asset = texture ?? line.texture;
      if (asset === null) tx.fail({ key: 'cmd.lines.textureRequired' });
      const a = checkImage(tx, asset);
      if (!isGifAsset(a)) tx.fail({ key: 'cmd.lines.notGif', params: { path: a.path } });
      tx.update('lines', lineId, { texture: asset, attachUI: null });
      dropKinds(['text', 'paint']);
      if (!hasTrack(tx, lineId, 'gif')) tx.update('lines', lineId, { gifAutoLoop: true });
      break;
    }
    case 'uiMover': {
      const element = attachUI ?? line.attachUI;
      if (element === null) tx.fail({ key: 'cmd.lines.uiElementRequired' });
      tx.update('lines', lineId, { texture: null, gifAutoLoop: false });
      dropKinds(['text', 'paint', 'gif']);
      applyUiMover(tx, tx.doc.lines.get(lineId) as Line, element);
      break;
    }
  }
  dropGifDataUnlessGif(tx, lineId);
};

const setAttachUI: LineCommands['setAttachUI'] = (tx: Tx, { line: lineId, element }) => {
  const line = lineOf(tx, lineId);
  if (element === null) {
    if (line.attachUI !== null) tx.update('lines', lineId, { attachUI: null });
    return;
  }
  applyUiMover(tx, line, element);
};

const setTexture: LineCommands['setTexture'] = (tx: Tx, { line: lineId, asset }) => {
  lineOf(tx, lineId);
  if (asset !== null) checkImage(tx, asset);
  applyTexture(tx, lineId, asset);
};

const setGifAutoLoop: LineCommands['setGifAutoLoop'] = (tx: Tx, { line: lineId, on }) => {
  const line = lineOf(tx, lineId);
  if (!on) {
    if (line.gifAutoLoop) tx.update('lines', lineId, { gifAutoLoop: false });
    return;
  }
  if (line.texture === null || !isGifAsset(tx.doc.assets.get(line.texture))) {
    tx.fail({ key: 'cmd.lines.gifAutoLoopNeedsGif' });
  }
  deletePropTracks(tx, lineId, 'gif');
  if (!line.gifAutoLoop) tx.update('lines', lineId, { gifAutoLoop: true });
};

export const lineTypeCommands = { setLineType, setAttachUI, setTexture, setGifAutoLoop } as const;
